import { IconPencil } from '@loadsmart/icons'
import { Button, Layout, Text } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-tokens'
import styled from 'styled-components'

import type { GateFeedFilter } from '../../../clients/type'

import { useYardStats } from '../hooks/useYardStats'
import type { AssetStats, BigNumber } from '../types'
import { getBigNumbersCount, getBigNumbersTitle } from '../utils'
import { ALL_ASSETS } from '../constants'

const BigNumberContainer = styled(Layout.Box)<{
  active: BigNumberBoxProps['active']
  isLoading: BigNumberBoxProps['isLoading']
}>`
  --m-box-background-color: ${(props) =>
    toCSSValue(props.active ? 'color-background-tertiary' : 'color-background-primary')};

  position: relative;
  cursor: ${(props) => (props.isLoading ? 'not-allowed' : 'pointer')};
  max-width: 150px;
  min-width: 150px;

  &:hover {
    --m-box-background-color: ${(props) =>
      toCSSValue(props.active ? 'color-background-highlight' : 'color-background-tertiary')};
  }
`

const StyledSwitcher = styled(Layout.Switcher)`
  align-items: center;
`

const StyledButton = styled(Button)`
  max-width: fit-content;
`

type BigNumberProps = {
  activeBigNumbers: BigNumber[]
  siteId?: string
  filters: GateFeedFilter[]
  onBigNumberClick: (bigNumberOption: string, filterId: string) => void
  handleOpenDrawer: () => void
  isTableLoading: boolean
}

type BigNumberBoxProps = {
  active: boolean
  label: string
  title: string
  id: string
  onClick: (bigNumberOption: string, filterId: string) => void
  isLoading: boolean
  stats: AssetStats[]
}

function BigNumberBox({ active, label, title, id, onClick, isLoading, stats }: BigNumberBoxProps) {
  let optionStats = title.includes(ALL_ASSETS)
    ? { name: ALL_ASSETS, count: '-' }
    : stats.find((stat) => stat.name === label)

  if (!optionStats) {
    return null
  }

  return (
    <BigNumberContainer
      active={active}
      borderWidth="border-thin"
      borderColor={active ? 'color-neutral-40' : 'color-border'}
      padding="spacing-4"
      onClick={() => (!isLoading ? onClick(title, id) : null)}
      isLoading={isLoading}
      aria-selected={active}
      aria-label={`${title} quick filter`}
    >
      <Layout.Stack gap="spacing-1">
        <Layout.Stack gap="none">
          <Text
            variant="body-sm"
            color={isLoading ? 'color-text-disabled' : 'color-text-secondary'}
          >
            {getBigNumbersTitle(title)}
          </Text>
          <Text
            variant="body-md-bold"
            color={isLoading ? 'color-text-disabled' : 'color-text-secondary'}
          >
            {getBigNumbersCount(stats, optionStats)}
          </Text>
        </Layout.Stack>
      </Layout.Stack>
    </BigNumberContainer>
  )
}

export function BigNumbers({
  activeBigNumbers,
  siteId,
  filters,
  onBigNumberClick,
  handleOpenDrawer,
  isTableLoading,
}: BigNumberProps) {
  siteId = siteId === 'all' ? undefined : siteId
  const { stats, isLoading } = useYardStats({ siteId })

  return (
    <>
      <Layout.Group justify="space-between" align="center">
        <StyledSwitcher gap="spacing-4" threshold="115px" limit={10}>
          {activeBigNumbers.map((option) => (
            <BigNumberBox
              key={option.name}
              active={filters.some((filter) => {
                if (option.name === ALL_ASSETS && filters.length === 1) {
                  return true
                }
                return filter.id === option.name
              })}
              title={option.name}
              label={option.label}
              stats={stats}
              id={option.id}
              onClick={onBigNumberClick}
              isLoading={isLoading || isTableLoading}
            />
          ))}
        </StyledSwitcher>
        <StyledButton
          variant="icon-secondary"
          size="small"
          onClick={handleOpenDrawer}
          disabled={isLoading}
        >
          <IconPencil />
        </StyledButton>
      </Layout.Group>
    </>
  )
}
